import type { Ref } from 'vue'
import type { RectType } from '../guide'
/**
 * 计算偏移量
 * @param {string} addend 加数 string
 * @param {string/number} augend 被加数
 * @returns {string} 和
 */
const calcPixel = (
  addend: string,
  augend: string | number,
  multiplier: number
): string => {
  return typeof augend === 'string'
    ? `calc(${addend} + ${multiplier * (Number.parseFloat(augend) / 100)}px)`
    : `calc(${addend} + ${augend}px)`
}

/**
 * 计算高亮区域/hint原点 位置
 * @param {HTMLElement} realDom DOM
 * @param {string} type  hint/default
 * @param {Array.<number/string>} offset 偏移量
 * @returns {width: string, height: string, top: string, left: string}
 */
export const calcHLStyle = ({
  realDom,
  type = 'default',
  offset = [0, 0],
  cacheDomRect,
  parent,
  positionType = 'absolute',
}: {
  realDom: HTMLElement
  type?: 'hint' | 'default'
  offset?: number[] | string[]
  cacheDomRect?: Ref<RectType | undefined>
  parent?: keyof HTMLElementTagNameMap
  positionType?: 'absolute' | 'fixed'
}) => {
  const [offsetX, offsetY] = offset || [0, 0]
  const { top, width, left, height } = realDom?.getBoundingClientRect() || {}
  const scrollTop =
    window.pageYOffset ||
    document.documentElement.scrollTop ||
    document.body.scrollTop
  const scrollLeft =
    window.pageXOffset ||
    document.documentElement.scrollLeft ||
    document.body.scrollLeft
  if (type === 'hint') {
    if (cacheDomRect) cacheDomRect.value = { top, width, left, height }
    // 5px -> 代表 hint 原点半径, 保证 fixed 定位 永远以圆心做为原点
    if (parent) {
      // 当组件接收到parent参数后, offset数据则根据target左上角开始计算
      return {
        left:
          typeof offsetX === 'string'
            ? `calc(${offsetX} - 5px)`
            : `${offsetX - 5}px`,
        top:
          typeof offsetY === 'string'
            ? `calc(${offsetY} - 5px)`
            : `${offsetY - 5}px`,
      }
    }
    return {
      left: calcPixel(`${left + scrollLeft - 5}px`, offsetX, width),
      top: calcPixel(`${top + scrollTop - 5}px`, offsetY, height),
    }
  }
  if (positionType === 'fixed') {
    return {
      width: `${width + 10}px`,
      left: calcPixel(`${left - 5}px`, offsetX, width),
      height: `${height + 8}px`,
      top: calcPixel(`${top - 4}px`, offsetY, height),
    }
  }
  return {
    width: `${width + 10}px`,
    left: calcPixel(`${left + scrollLeft - 5}px`, offsetX, width),
    height: `${height + 8}px`,
    top: calcPixel(`${top + scrollTop - 4}px`, offsetY, height),
  }
}

/**
 * 判断元素是否在可视区域, 不在的话, 滚动到可视区域
 */
const inInViewport = (realDom: HTMLElement): boolean => {
  const rect = realDom.getBoundingClientRect()
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  )
}
export const scrollToViewport = (realDom: HTMLElement) => {
  const isIn = inInViewport(realDom)
  if (isIn) return
  realDom?.scrollIntoView({
    block: 'center',
    inline: 'center',
  })
}
